import React, { useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import filter from 'lodash/filter';
import find from 'lodash/find';
import reject from 'lodash/reject';

import { FormSubtitle } from '../styled';
import EmailField from './EmailField';
import { UAppEmail } from '../../models/AugmentedClasses';
import { OtherPlus } from './ContactForm';

import { TAppContact, TAppEmail } from 'TProtocol/common/models';

interface Props {
  contact: TAppContact;
  onChange: (contact: TAppContact) => void;
  isSingle?: boolean;
  autocomplete?: boolean;
}

export enum IUEmailLabel {
  PersonalEmail = 1,
  ProfessionalEmail,
  OtherEmail
}

export const EmailLabels: {
  [key in IUEmailLabel]: string;
} = {
  [IUEmailLabel.PersonalEmail]: 'Personal',
  [IUEmailLabel.ProfessionalEmail]: 'Professional',
  [IUEmailLabel.OtherEmail]: 'Other'
};

export interface EmailObject {
  email: UAppEmail;
  label: IUEmailLabel;
}

const EmailFields = ({ contact, onChange, isSingle = false, autocomplete = false }: Props) => {
  const [emails, setEmails] = useState<EmailObject[]>([]);

  useEffect(() => {
    const emails: EmailObject[] = [];
    if (contact.personalEmail) {
      emails.push({
        email: contact.personalEmail,
        label: IUEmailLabel.PersonalEmail
      });
    }
    if (contact.professionalEmail) {
      emails.push({
        email: contact.professionalEmail,
        label: IUEmailLabel.ProfessionalEmail
      });
    }

    if (contact.otherEmails) {
      contact.otherEmails.forEach((email) => {
        emails.push({
          email,
          label: IUEmailLabel.OtherEmail
        });
      });
    }

    setEmails(emails);
  }, []);

  const handleDelete = (email: EmailObject) => {
    removeEmail(email);
    setEmails(reject(emails, email));
    onChange(contact);
  };

  const handleChange = (email: EmailObject, value: string) => {
    email.email.displayEmail = value;
    email.email.emailAddress = value;

    if (email.label === IUEmailLabel.PersonalEmail && contact.personalEmail) {
      contact.personalEmail.displayEmail = value;
      contact.personalEmail.emailAddress = value;
    } else if (email.label === IUEmailLabel.ProfessionalEmail && contact.professionalEmail) {
      contact.professionalEmail.displayEmail = value;
      contact.professionalEmail.emailAddress = value;
    } else if (email.label === IUEmailLabel.OtherEmail) {
      const otherEmail = find<UAppEmail>(contact.otherEmails, { _key: email.email._key });
      if (otherEmail) {
        otherEmail.displayEmail = value;
        otherEmail.emailAddress = value;
      }
    }

    onChange(contact);
  };

  const handleLabelChange = (email: EmailObject, previousLabel: IUEmailLabel) => {
    removeEmail({
      email: email.email,
      label: previousLabel
    });
    moveExistingEmailToOther(email.label);
    updateEmails(email);
    updateEmailField(email.label, email.email);
    setEmails([...emails]);
    onChange(contact);
  };

  const addEmail = () => {
    const email = new UAppEmail();
    let label = IUEmailLabel.OtherEmail;

    if (!contact.personalEmail) {
      contact.personalEmail = email;
      label = IUEmailLabel.PersonalEmail;
    } else if (!contact.professionalEmail) {
      contact.professionalEmail = email;
      label = IUEmailLabel.ProfessionalEmail;
    } else {
      if (!contact.otherEmails) {
        contact.otherEmails = [];
      }
      contact.otherEmails.push(email);
    }

    emails.push({ email, label });

    onChange(contact);
  };

  const removeEmail = (email: EmailObject) => {
    if (email.label === IUEmailLabel.OtherEmail) {
      removeOtherEmail(email.email);
    } else {
      updateEmailField(email.label, undefined);
    }
  };

  const removeOtherEmail = (email: TAppEmail) => {
    // Have to find the email by value, as there is no other identifier we can use.
    let found = false;
    contact.otherEmails = filter(contact.otherEmails, (otherEmail) => {
      if (!found && email.emailAddress === otherEmail.emailAddress) {
        found = true;
        return false;
      }
      return true;
    });
  };

  const moveExistingEmailToOther = (label: IUEmailLabel) => {
    let existingEmail: TAppEmail | undefined;
    if (label === IUEmailLabel.PersonalEmail) {
      existingEmail = contact.personalEmail;
    } else if (label === IUEmailLabel.ProfessionalEmail) {
      existingEmail = contact.professionalEmail;
    }

    if (existingEmail !== undefined) {
      if (!contact.otherEmails) {
        contact.otherEmails = [];
      }
      contact.otherEmails.push(existingEmail);
    }

    const existingEmailObject = find(emails, { label });
    if (existingEmailObject) {
      existingEmailObject.label = IUEmailLabel.OtherEmail;
    }
  };

  const updateEmails = (email: EmailObject) => {
    const existingEmail = emails.find((e) => e.email._key === email.email._key);
    if (existingEmail) {
      existingEmail.email = email.email;
      existingEmail.label = email.label;
    }
  };

  const updateEmailField = (label: IUEmailLabel, email: TAppEmail | undefined) => {
    if (label === IUEmailLabel.PersonalEmail) {
      contact.personalEmail = email;
    } else if (label === IUEmailLabel.ProfessionalEmail) {
      contact.professionalEmail = email;
    } else if (label === IUEmailLabel.OtherEmail && email !== undefined) {
      if (!contact.otherEmails) {
        contact.otherEmails = [];
      }
      contact.otherEmails.push(email);
    }
  };

  return <>
    <FormSubtitle>{ isSingle ? 'Email' : 'Emails' }</FormSubtitle>

    {
      emails.map((email) => {
        return <Row key={email.email._key}>
          <Col>
            <EmailField
              contact={contact}
              email={email}
              hideLabel={isSingle}
              autocomplete={autocomplete}
              handleDelete={() => {
                handleDelete(email);
              }}
              handleChange={(value) => {
                handleChange(email, value);
              }}
              handleLabelChange={handleLabelChange}
            />
          </Col>
        </Row>;
      })
    }

    {
      isSingle ? null : <div onClick={addEmail}>
        <OtherPlus>+</OtherPlus> Email
      </div>
    }
  </>;
};

export default EmailFields;
